import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { publisherClient as pubClient } from "@/lib/publisherApi";

/**
 * In-context nudge for verified publishers viewing their *own* claimed app
 * on the public detail page. Only renders when:
 *   1. The visitor is authenticated as a publisher (cookie session active)
 *   2. The visitor's email matches `app.verified_owner_email`
 *   3. The app's content-score is below the "good" threshold (< 75)
 *
 * Silent fallback: if any of these fail (no session, wrong owner, score OK),
 * the component renders nothing — so public visitors never see it.
 */
const PublisherNudge = ({ app, isNl }) => {
  const [match, setMatch] = useState(false);
  const [score, setScore] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!app?.verified_owner_email || !app?.slug) return;
    (async () => {
      try {
        const me = await pubClient.get("/publisher/me").then((r) => r.data);
        if (cancelled) return;
        const myEmail = (me?.email || "").toLowerCase();
        const ownerEmail = (app.verified_owner_email || "").toLowerCase();
        if (!myEmail || myEmail !== ownerEmail) return;
        setMatch(true);
        // Fetch the per-app content score
        const cq = await pubClient
          .get(`/publisher/apps/${app.slug}/content-quality`)
          .then((r) => r.data)
          .catch(() => null);
        if (!cancelled && cq) setScore(cq);
      } catch {
        /* not logged in or fetch failed → silently hide */
      }
    })();
    return () => { cancelled = true; };
  }, [app?.slug, app?.verified_owner_email]);

  if (!match || !score) return null;
  const pct = Math.max(0, Math.min(100, Math.round(score.score || 0)));
  // Only nudge when content is meaningfully incomplete
  if (pct >= 75) return null;

  const headline = isNl
    ? `Je profiel is ${pct}% compleet — maak het af`
    : `Your profile is ${pct}% complete — finish it now`;
  const sub = isNl
    ? "Listings met volledige beschrijving + 3+ screenshots krijgen gemiddeld 2,3× meer clicks en ranken hoger in de Top 100."
    : "Listings with full description + 3+ screenshots get 2.3× more clicks on average and rank higher in our Top 100.";

  // Surface the top 2-3 concrete recommendations so the publisher knows what to fix
  const tips = Array.isArray(score.recommendations) ? score.recommendations.slice(0, 3) : [];

  return (
    <div
      data-testid="publisher-nudge"
      className="mb-6 flex items-start gap-4 bg-gradient-to-br from-violet-50 via-white to-sky-50 border border-violet-200 rounded-2xl p-4 md:p-5 shadow-sm"
    >
      <div className="w-10 h-10 shrink-0 bg-violet-500 text-white rounded-xl flex items-center justify-center">
        <Sparkles size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-[10px] font-bold uppercase tracking-widest text-violet-700">
            {isNl ? "Voor jou — eigenaar" : "Just for you — owner"}
          </span>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200">
            {pct}/100
          </span>
        </div>
        <div className="font-bold text-[#0F172A] text-base md:text-lg leading-tight" data-testid="publisher-nudge-headline">
          {headline}
        </div>
        <p className="text-xs md:text-sm text-[#475569] mt-1 leading-relaxed">{sub}</p>
        {tips.length > 0 && (
          <ul className="mt-2 text-[12px] text-[#0F172A] space-y-0.5">
            {tips.map((t, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-violet-500 mt-0.5">•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 h-1.5 bg-white rounded-full overflow-hidden border border-violet-100" aria-hidden>
          <div className="h-full bg-gradient-to-r from-violet-500 to-sky-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <Link
        to="/publisher/dashboard"
        className="shrink-0 self-center inline-flex items-center gap-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs md:text-sm font-semibold px-3 md:px-4 py-2 rounded-full transition-colors"
        data-testid="publisher-nudge-cta"
      >
        {isNl ? "Werk af" : "Finish now"} <ArrowRight size={12} />
      </Link>
    </div>
  );
};

export default PublisherNudge;
